import React, { useState, useMemo } from 'react';
import { I18n } from 'react-redux-i18n';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { Flex, Input, Tag, Button } from 'antd';
import styled from 'styled-components';

import Popup from 'components/Popup';

import {
  useArticleHeader,
  useCreateTagMutation
} from 'apiTyped/hooks/articles';

const Container = styled.div`
  .block-tags {
    max-height: 200px;
    overflow-y: auto;
    margin: 20px 0;
  }

  .empty-tags {
    justify-content: center;
    width: 100%;
  }

  .block-new-tags {
    height: 24px;
    margin: 20px 0;
  }

  .create-tag-button {
    width: 100%;
    text-align: left;
    height: auto;
    padding: 8px 12px;
    border: 1px dashed #d9d9d9;
    background: #fafafa;

    &:hover {
      border-color: #40a9ff;
      color: #40a9ff;
    }
  }
`;

type TagsModalProps = {
  open: boolean;
  loading: boolean;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (tagsToRemove: number[]) => void;
};

type Option = {
  label: string;
  value: number;
};

const GlobalTagsModal = ({
  open,
  loading,
  onClose,
  onSubmit
}: TagsModalProps) => {
  const { articleHeader } = useArticleHeader();
  const createTagMutation = useCreateTagMutation();
  const availableTags: Option[] =
    articleHeader?.tags.map((tag) => ({
      value: tag.id,
      label: tag.name
    })) || [];

  const defaultTagsOtpions = [
    { label: I18n.t('media.optionTags.isFavorite'), value: 'is_favorite' },
    { label: I18n.t('media.optionTags.isFeatured'), value: 'is_featured' },
    { label: I18n.t('media.optionTags.isPaper'), value: 'is_paper' }
  ];

  const [tagsToRemove, setTagsToRemove] = useState<number[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isCreatingTag, setIsCreatingTag] = useState(false);

  const removeTag = (value: number) => {
    const updatedTagsToRemove = [...tagsToRemove];
    updatedTagsToRemove.push(value);
    setTagsToRemove([...new Set(updatedTagsToRemove)]);
  };

  const addNewTag = async (tagName: string) => {
    if (tagName.trim()) {
      setIsCreatingTag(true);
      try {
        await createTagMutation.mutateAsync({ name: tagName.trim() });
        setSearchValue('');
      } catch (error) {
        // Handle error (e.g., show notification)
      } finally {
        setIsCreatingTag(false);
      }
    }
  };

  const handleSubmit = async () => {
    onSubmit(tagsToRemove);
    setTagsToRemove([]);
    onClose();
  };

  const defaultFiltredTags = useMemo(() => {
    if (searchValue.length < 2) return defaultTagsOtpions;
    return defaultTagsOtpions.filter((item) =>
      item.label.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [searchValue]);

  const filteredTags = useMemo(() => {
    if (searchValue.length < 2) return availableTags;
    return availableTags.filter((item) =>
      item.label.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [availableTags, searchValue]);

  const hasNoResults =
    [...defaultFiltredTags, ...filteredTags].length === 0 &&
    searchValue.trim().length > 0;

  return (
    <Popup
      open={open}
      title={I18n.t('media.globalTagsModal.title')}
      onClose={onClose}
      onOk={() => handleSubmit()}
      disableOk={loading}
    >
      <Container>
        <Input
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder={I18n.t('media.globalTagsModal.searchTag')}
          prefix={<SearchOutlined />}
          style={{ borderRadius: 4 }}
          allowClear
        />
        <Flex wrap="wrap" gap="small" className="block-tags">
          {defaultFiltredTags.map((tag) => (
            <Tag bordered={false} key={tag.value}>
              {tag.label}
            </Tag>
          ))}
          {filteredTags.map((tag) => (
            <Tag
              bordered={false}
              key={tag.value}
              closable
              onClose={() => removeTag(tag.value)}
            >
              {tag.label}
            </Tag>
          ))}
          {hasNoResults && (
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              className="create-tag-button"
              onClick={() => addNewTag(searchValue)}
              loading={isCreatingTag}
            >
              {I18n.t('media.globalTagsModal.btnAdd')}: &quot;{searchValue}
              &quot;
            </Button>
          )}
        </Flex>
      </Container>
    </Popup>
  );
};

export default GlobalTagsModal;
